import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Panel, PanelHeader, PanelBody } from '../../components/panel/panel.jsx';
import axiosInstance from '../../config/axiosApi/axiosApi.jsx';
import APIs from '../../Constants/apis.js';
import Preloader from '../../components/CustomComponents/preLoad/preLoader.js';
import showErrorToast from '../../components/CustomComponents/toast/toast.js';
import { logout } from '../../app/auth/authSlice.js';

function Home() {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const [loading, setLoading] = useState(true);
	const [stats, setStats] = useState({
		companies: { total: 0, active: 0 },
		packages: { total: 0, active: 0 },
		addons: { total: 0, active: 0 },
		subscriptions: { total: 0, active: 0 },
		invoices: { total: 0, paid: 0, pending: 0, overdue: 0 }
	});

	const fetchDashboardStats = useCallback(async () => {
		try {
			setLoading(true);

			const response = await axiosInstance.get(APIs.GET_DASHBOARD_STATS);

			if (response.data.status && response.data.data) {
				const data = response.data.data;
				setStats({
					companies: { 
						total: data.totalCompanies || 0, 
						active: data.activeCompanies || 0 
					},
					packages: { 
						total: data.totalPackages || 0, 
						active: data.activePackages || 0 
					},
					addons: { 
						total: data.totalAddons || 0, 
						active: data.activeAddons || 0 
					},
					subscriptions: { 
						total: data.totalSubscriptions || 0, 
						active: data.activeSubscriptions || 0 
					},
					invoices: { 
						total: data.totalInvoices || 0, 
						paid: data.paidInvoices || 0, 
						pending: data.pendingInvoices || 0, 
						overdue: data.overdueInvoices || 0 
					}
				});
			}

			setLoading(false);
		} catch (err) {
			console.error(err);
			setLoading(false);
			if (err.response && err.response.status === 401) {
				dispatch(logout());
				setTimeout(() => {
					navigate("/user/login", { replace: true });
				}, 1000);
			} else {
				showErrorToast("Failed to fetch dashboard statistics");
			}
		}
	}, [dispatch, navigate]);

	useEffect(() => {
		fetchDashboardStats();
	}, [fetchDashboardStats]);

	const StatCard = ({ title, total, active, icon, color, link }) => (
		<div className="col-xl-4 col-md-6">
			<div className="widget widget-stats" style={{ borderLeft: `3px solid ${color}` }}>
				<div className="stats-icon" style={{ backgroundColor: `${color}15` }}>
					<i className={`fa ${icon}`} style={{ color: color, fontSize: '2.5rem' }}></i>
				</div>
				<div className="stats-info">
					<h3 style={{ 
						fontSize: '1.3rem', 
						fontWeight: '700', 
						marginBottom: '0.8rem',
						color: color,
						textTransform: 'uppercase',
						letterSpacing: '0.5px'
					}}>
						{title}
					</h3>
					<p style={{ marginBottom: '0.3rem' }}>
						<span style={{ fontSize: '0.75rem', color: '#7c7c7c', textTransform: 'uppercase' }}>Total</span>
					</p>
					<p style={{ marginBottom: '0.5rem' }}>
						<span style={{ fontSize: '2rem', fontWeight: 'bold', color: '#2d353c' }}>{total}</span>
					</p>
					<p style={{ marginBottom: '0.2rem' }}>
						<span style={{ fontSize: '0.75rem', color: '#7c7c7c', textTransform: 'uppercase' }}>Active</span>
					</p>
					<p style={{ marginBottom: '0' }}>
						<span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#00acac' }}>{active}</span>
					</p>
				</div>
				<div className="stats-link">
					<Link to={link}>
						View Details <i className="fa fa-arrow-alt-circle-right"></i>
					</Link>
				</div>
			</div>
		</div>
	);

	return (
		<div>
			{loading && <Preloader />}
			<ol className="breadcrumb float-xl-end">
				<li className="breadcrumb-item active">Dashboard</li>
			</ol>
			<h1 className="page-header">Dashboard <small>Overview & Statistics</small></h1>

			<div className="row">
				<StatCard
					title="Companies"
					total={stats.companies.total}
					active={stats.companies.active}
					icon="fa-building"
					color="#348fe2"
					link="/company/manageCompany"
				/>
				<StatCard
					title="Packages"
					total={stats.packages.total}
					active={stats.packages.active}
					icon="fa-box"
					color="#00acac"
					link="/packages/managePackages"
				/>
				<StatCard
					title="Addons"
					total={stats.addons.total}
					active={stats.addons.active}
					icon="fa-puzzle-piece"
					color="#f59c1a"
					link="/addons/manageAddons"
				/>
		</div>

			<div className="row">
				<div className="col-xl-4">
					<Panel>
						<PanelHeader>Subscription System</PanelHeader>
						<PanelBody>
							<div className="list-group list-group-flush">
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-box text-info me-2"></i>
										<strong>Total Packages</strong>
									</div>
									<span className="badge bg-info rounded-pill">{stats.packages.total}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-check-circle text-success me-2"></i>
										Active Packages
									</div>
									<span className="badge bg-success rounded-pill">{stats.packages.active}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-puzzle-piece text-warning me-2"></i>
										<strong>Total Addons</strong>
									</div>
									<span className="badge bg-warning rounded-pill">{stats.addons.total}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-check-circle text-success me-2"></i>
										Active Addons
									</div>
									<span className="badge bg-success rounded-pill">{stats.addons.active}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-file-contract text-primary me-2"></i>
										<strong>Total Subscriptions</strong>
									</div>
									<span className="badge bg-primary rounded-pill">{stats.subscriptions.total}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-check-circle text-success me-2"></i>
										Active Subscriptions
									</div>
									<span className="badge bg-success rounded-pill">{stats.subscriptions.active}</span>
								</div>
							</div>
						</PanelBody>
					</Panel>
				</div>
				<div className="col-xl-4">
					<Panel>
						<PanelHeader>Companies & Content</PanelHeader>
						<PanelBody>
							<div className="list-group list-group-flush">
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-building text-primary me-2"></i>
										<strong>Total Companies</strong>
									</div>
									<span className="badge bg-primary rounded-pill">{stats.companies.total}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-check-circle text-success me-2"></i>
										Active Companies
									</div>
									<span className="badge bg-success rounded-pill">{stats.companies.active}</span>
								</div>
							</div>
						</PanelBody>
					</Panel>
				</div>
				<div className="col-xl-4">
					<Panel>
						<PanelHeader>Invoice Management</PanelHeader>
						<PanelBody>
							<div className="list-group list-group-flush">
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-file-invoice text-primary me-2"></i>
										<strong>Total Invoices</strong>
									</div>
									<span className="badge bg-primary rounded-pill">{stats.invoices.total}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-check-circle text-success me-2"></i>
										Paid Invoices
									</div>
									<span className="badge bg-success rounded-pill">{stats.invoices.paid}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-clock text-warning me-2"></i>
										Pending Invoices
									</div>
									<span className="badge bg-warning rounded-pill">{stats.invoices.pending}</span>
								</div>
								<div className="list-group-item d-flex justify-content-between align-items-center">
									<div>
										<i className="fa fa-exclamation-triangle text-danger me-2"></i>
										Overdue Invoices
									</div>
									<span className="badge bg-danger rounded-pill">{stats.invoices.overdue}</span>
								</div>
							</div>
						</PanelBody>
					</Panel>
				</div>
			</div>
		</div>
	)
}

export default Home;
